// Google Analytics utility for cart and product analytics events
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    clarity: (...args: any[]) => void;
  }
}

/**
 * Set a custom tag on the current Clarity session.
 * Used to filter recordings by key attributes (e.g. chat_used, last_query).
 */
const setClarityTag = (key: string, value: string) => {
  if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
    window.clarity('set', key, value);
  }
};

/**
 * Returns the Clarity session recording URL for the current session, or null if unavailable.
 * Format: https://clarity.microsoft.com/app/{projectId}/session/{sessionId}
 */
export const getClaritySessionUrl = (): string | null => {
  try {
    if (typeof window !== 'undefined' && typeof window.clarity === 'function') {
      const meta = window.clarity('metadata') as { projectId?: string; sessionId?: string } | undefined;
      if (meta?.projectId && meta?.sessionId) {
        return `https://clarity.microsoft.com/app/${meta.projectId}/session/${meta.sessionId}`;
      }
    }
  } catch {
    // Clarity not ready yet — safe to ignore
  }
  return null;
};

/**
 * Tag the current Clarity session when a user sends a chat query.
 * Enables filtering recordings to "sessions where chat was used".
 */
export const tagClarityWithQuery = (query: string, menuId: string) => {
  setClarityTag('chat_used', 'true');
  setClarityTag('chat_menu_id', menuId);
  // Truncate to 150 chars — Clarity tag value limit
  setClarityTag('last_query', query.substring(0, 150));
};

type AnalyticsParams = Record<string, string | number | boolean | null | undefined | object | Array<unknown>>;

/**
 * Generic GA4 event sender with runtime safety checks.
 */
export const trackEvent = (eventName: string, params: AnalyticsParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// Cart item interface for analytics
interface CartItemAnalytics {
  id: string;
  name: string;
  price: number;
  quantity: number;
  category?: string;
}

// Track when an item is added to cart
export const trackAddToCart = (item: CartItemAnalytics, menuId: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'add_to_cart', {
      currency: 'JPY',
      value: item.price * item.quantity,
      menu_id: menuId,
      items: [{
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
        item_category: item.category || 'Unknown'
      }]
    });
    
    console.log('Analytics: Item added to cart', {
      menu_id: menuId,
      item_id: item.id,
      item_name: item.name,
      quantity: item.quantity,
      price: item.price
    });
  }
};

// Track when an item is removed from cart
export const trackRemoveFromCart = (item: CartItemAnalytics, menuId: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'remove_from_cart', {
      currency: 'JPY',
      value: item.price * item.quantity,
      menu_id: menuId,
      items: [{
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
        item_category: item.category || 'Unknown'
      }]
    });
    
    console.log('Analytics: Item removed from cart', {
      menu_id: menuId,
      item_id: item.id,
      item_name: item.name,
      quantity: item.quantity,
      price: item.price
    });
  }
};

// Track when cart is opened
export const trackCartOpened = (items: CartItemAnalytics[], menuId: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    
    window.gtag('event', 'view_cart', {
      currency: 'JPY',
      value: totalValue,
      menu_id: menuId,
      items: items.map(item => ({
        item_id: item.id,
        item_name: item.name,
        price: item.price,
        quantity: item.quantity,
        item_category: item.category || 'Unknown'
      }))
    });
    
    // Custom event for cart contents
    window.gtag('event', 'cart_opened', {
      menu_id: menuId,
      cart_total_value: totalValue,
      cart_total_items: totalItems,
      cart_unique_items: items.length,
      cart_contents: items.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price
      }))
    });
    
    console.log('Analytics: Cart opened', {
      menu_id: menuId,
      total_value: totalValue,
      total_items: totalItems,
      unique_items: items.length,
      items: items.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price
      }))
    });
  }
};

// Track when cart is cleared
export const trackCartCleared = (items: CartItemAnalytics[], menuId: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
    
    window.gtag('event', 'cart_cleared', {
      menu_id: menuId,
      cart_total_value: totalValue,
      cart_total_items: totalItems,
      cart_unique_items: items.length,
      cart_contents: items.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price
      }))
    });
    
    console.log('Analytics: Cart cleared', {
      menu_id: menuId,
      total_value: totalValue,
      total_items: totalItems,
      unique_items: items.length,
      items: items.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price
      }))
    });
  }
};

// Track when a user views a menu page
export const trackMenuViewed = (params: {
  menuId: string;
  restaurantName?: string;
  language: string;
}) => {
  trackEvent('menu_viewed', {
    menu_id: params.menuId,
    restaurant_name: params.restaurantName || 'Unknown',
    language: params.language
  });
};

// Track when a user opens a specific menu item
export const trackMenuItemOpened = (params: {
  menuId: string;
  menuItemId: string;
  language: string;
}) => {
  trackEvent('menu_item_opened', {
    menu_id: params.menuId,
    menu_item_id: params.menuItemId,
    language: params.language
  });
};

// Track menu chat message sent
export const trackChatMessageSent = (params: {
  menuId: string;
  language: string;
  contextType: 'menu' | 'menu_item';
  messageLength: number;
}) => {
  trackEvent('chat_message_sent', {
    menu_id: params.menuId,
    language: params.language,
    context_type: params.contextType,
    message_length: params.messageLength
  });
};

// Track menu chat response received
export const trackChatResponseReceived = (params: {
  menuId: string;
  language: string;
  contextType: 'menu' | 'menu_item';
  latencyMs: number;
}) => {
  trackEvent('chat_response_received', {
    menu_id: params.menuId,
    language: params.language,
    context_type: params.contextType,
    latency_ms: params.latencyMs
  });
};

// Track language changes on the menu page
export const trackLanguageChanged = (params: {
  menuId?: string;
  fromLanguage: string;
  toLanguage: string;
}) => {
  trackEvent('language_changed', {
    menu_id: params.menuId || 'unknown',
    from_language: params.fromLanguage,
    to_language: params.toLanguage
  });
};